import type {
  Position,
  TemplateAttribute,
} from '@/services/hooks/template_attributes'
import {
  isKonvaAnnotationTemplate,
  type KonvaAnnotationTemplate,
} from './Annotations/annotationSchema'

export const DEFAULT_TEMPLATE_ATTRIBUTE_Z = 1000

export const getTemplateAttributeZ = (
  templateAttribute: TemplateAttribute<any> | undefined | null,
) => {
  const templateValues = templateAttribute?.template_values

  if (isKonvaAnnotationTemplate(templateValues)) {
    return (
      templateValues.geometry.z ??
      (templateValues as KonvaAnnotationTemplate & { position?: Position })
        .position?.z ??
      DEFAULT_TEMPLATE_ATTRIBUTE_Z
    )
  }

  return templateValues?.position?.z ?? DEFAULT_TEMPLATE_ATTRIBUTE_Z
}

export const setTemplateAttributeZ = (templateValues: any, z: number) => {
  if (isKonvaAnnotationTemplate(templateValues)) {
    const konvaTemplateValues = templateValues as KonvaAnnotationTemplate & {
      position?: Position
    }

    return {
      ...konvaTemplateValues,
      geometry: {
        ...konvaTemplateValues.geometry,
        z,
      },
      position: {
        ...(konvaTemplateValues.position || {}),
        z,
      },
    }
  }

  return {
    ...templateValues,
    position: {
      ...(templateValues?.position || {}),
      z,
    },
  }
}

export type KonvaAnnotationStackBand = {
  id: string
  templateAttributes: TemplateAttribute<any>[]
  z: number
}

const hasNonAnnotationZBetween = ({
  fromZ,
  nonAnnotationZValues,
  toZ,
}: {
  fromZ: number
  nonAnnotationZValues: number[]
  toZ: number
}) => {
  let low = 0
  let high = nonAnnotationZValues.length

  while (low < high) {
    const mid = Math.floor((low + high) / 2)

    if (nonAnnotationZValues[mid] < fromZ) {
      low = mid + 1
    } else {
      high = mid
    }
  }

  return low < nonAnnotationZValues.length && nonAnnotationZValues[low] < toZ
}

export const getKonvaAnnotationStackBands = (
  templateAttributes: TemplateAttribute<any>[],
): KonvaAnnotationStackBand[] => {
  const nonAnnotationZValues = templateAttributes
    .filter((templateAttribute) => templateAttribute.data_type !== 'annotation')
    .map(getTemplateAttributeZ)
    .sort((a, b) => a - b)

  const annotationTemplateAttributes = templateAttributes
    .filter((templateAttribute) => templateAttribute.data_type === 'annotation')
    .sort((a, b) => getTemplateAttributeZ(a) - getTemplateAttributeZ(b))

  // Consecutive annotations can share one canvas until a DOM element's z-index
  // falls between them. At that point they need separate CSS stacking contexts.
  return annotationTemplateAttributes.reduce<KonvaAnnotationStackBand[]>(
    (bands, templateAttribute) => {
      const z = getTemplateAttributeZ(templateAttribute)
      const currentBand = bands[bands.length - 1]

      if (!currentBand) {
        return [
          {
            id: `konva-annotation-band-${z}`,
            templateAttributes: [templateAttribute],
            z,
          },
        ]
      }

      const currentBandMaxZ = getTemplateAttributeZ(
        currentBand.templateAttributes[
          currentBand.templateAttributes.length - 1
        ],
      )
      const mustSplitBand = hasNonAnnotationZBetween({
        fromZ: currentBandMaxZ,
        nonAnnotationZValues,
        toZ: z,
      })

      if (mustSplitBand) {
        return [
          ...bands,
          {
            id: `konva-annotation-band-${z}`,
            templateAttributes: [templateAttribute],
            z,
          },
        ]
      }

      currentBand.templateAttributes.push(templateAttribute)
      return bands
    },
    [],
  )
}
